"use client";

import React from "react";
import { CreditCard, Landmark, Wallet, Truck, Clock, ShieldAlert, RotateCcw, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pricingTiers = [{
  id: 1,
  plan: "Starter Deposit",
  price: "£10",
  cycle: "minimum"
}, {
  id: 2,
  plan: "Active Wallet",
  price: "£50",
  cycle: "recommended"
}, {
  id: 3,
  plan: "Corporate Fund",
  price: "£250+",
  cycle: "bespoke"
}];
const paymentMethods = [{
  id: 1,
  name: "Digital Wallets (Apple Pay, Google Pay, PayPal)",
  icon: Wallet
}, {
  id: 2,
  name: "Major Cards (Visa, Mastercard, Amex)",
  icon: CreditCard
}, {
  id: 3,
  name: "Secure Bank Transfers (BACS & Wire)",
  icon: Landmark
}];
const deliveryTerms = [{
  id: 1,
  title: "Instant Wallet Access",
  desc: "Digital funds are credited to your account immediately upon secure payment authorisation and verification.",
  icon: Clock
}, {
  id: 2,
  title: "Service Provisioning",
  desc: "Managed campaigns and custom service setups are typically finalised within 24 hours of funding.",
  icon: Truck
}];
const refundTerms = [{
  id: 1,
  title: "30-Day Notice Period",
  desc: "Service cancellations require a formal 30-day written notice period for account reconciliation and closure.",
  icon: RotateCcw
}, {
  id: 2,
  title: "Balance Protection",
  desc: "Unused digital wallet balances remain protected and are subject to our standard exit and refund protocols.",
  icon: ShieldAlert
}];
export default function PricingPaymentRefundPolicy() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="184122" id="payments-and-cancellations" className="py-16 md:py-24 bg-[#050b1a] text-slate-50 overflow-hidden">
      <div ref={sectionRef} className={`container mx-auto px-4 md:px-6 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="max-w-3xl mb-12">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 uppercase font-sans text-slate-50">
            Payments & <span className="text-[#C79100]">Cancellations</span>
          </h2>
          <p className="text-lg text-slate-400 font-sans">
            Our premium authority framework utilises a secure digital wallet system, ensuring total transparency for your deposits and a structured 30-day notice period.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          
          {}
          <Card className="h-full border-white/10 shadow-2xl backdrop-blur-md bg-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-sans text-slate-50">Deposit Structure</CardTitle>
              <CardDescription className="text-slate-400">Standard thresholds for digital wallet activation.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader className="border-white/10">
                  <TableRow className="hover:bg-transparent border-white/10">
                    <TableHead className="w-[150px] text-slate-300">Tier</TableHead>
                    <TableHead className="text-slate-300">Amount</TableHead>
                    <TableHead className="text-right text-slate-300">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pricingTiers.map((tier, i) => <TableRow key={tier.id} data-index={i} className="border-white/5 hover:bg-white/5">
                      <TableCell className="font-medium text-slate-200">{tier.plan}</TableCell>
                      <TableCell className="text-[#C79100] font-bold">{tier.price}</TableCell>
                      <TableCell className="text-right text-slate-400 text-sm italic">{tier.cycle}</TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
              <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <span className="text-sm text-slate-400">Require specific funding?</span>
                <Button asChild variant="outline" size="sm" className="border-[#C79100] text-[#C79100] hover:bg-[#C79100] hover:text-black">
                  <Link to="/PricingPage">
                    View Pricing <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="h-full border-white/10 shadow-2xl backdrop-blur-md bg-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-sans text-slate-50">Payment Ecosystem</CardTitle>
              <CardDescription className="text-slate-400">Encrypted gateways for secure wallet top-ups.</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-6">
                {paymentMethods.map((method, i) => {
                const Icon = method.icon;
                return <li key={method.id} data-index={i} className="flex items-start gap-4">
                      <div className="bg-[#C79100]/20 p-2.5 rounded-lg text-[#C79100] shrink-0 border border-[#C79100]/30">
                        <Icon className="size-5" />
                      </div>
                      <div className="pt-1.5">
                        <span className="font-semibold text-slate-100">{method.name}</span>
                      </div>
                    </li>;
              })}
              </ul>
              <div className="mt-8 p-4 bg-slate-900/50 rounded-lg border border-white/5">
                <p className="text-sm text-slate-400 leading-relaxed">
                  All transactions utilise high-level SSL encryption. We prioritize your financial security by partnering with industry-leading payment processors for all wallet deposits.
                </p>
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="h-full border-white/10 shadow-2xl backdrop-blur-md bg-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-sans text-slate-50">Wallet Activation</CardTitle>
              <CardDescription className="text-slate-400">Timelines for digital credit and service launch.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {deliveryTerms.map((term, i) => {
                const Icon = term.icon;
                return <div key={term.id} data-index={i} className="flex gap-4">
                      <div className="mt-1 text-slate-400 shrink-0">
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#C79100] mb-1 uppercase text-sm tracking-wider">{term.title}</h4>
                        <p className="text-sm text-slate-300 leading-relaxed">{term.desc}</p>
                      </div>
                    </div>;
              })}
              </div>
            </CardContent>
          </Card>

          {}
          <Card className="h-full border-white/10 shadow-2xl backdrop-blur-md bg-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-sans text-slate-50">Notice & Exit Terms</CardTitle>
              <CardDescription className="text-slate-400">Clear protocols for account management and closure.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {refundTerms.map((term, i) => {
                const Icon = term.icon;
                return <div key={term.id} data-index={i} className="flex gap-4">
                      <div className="mt-1 text-[#C79100] shrink-0">
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-100 mb-1 uppercase text-sm tracking-wider">{term.title}</h4>
                        <p className="text-sm text-slate-300 leading-relaxed">{term.desc}</p>
                      </div>
                    </div>;
              })}
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-sm text-slate-400 mb-4">
                  To submit a 30-day notice or inquire about wallet balances, please contact our administrative support team.
                </p>
                <Button asChild className="w-full sm:w-auto bg-gradient-to-r from-[#C79100] to-[#E6B800] text-black font-bold hover:opacity-90">
                  <Link to="/PricingPage">
                    Manage Account
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </section>;
}